/**
 * Mi Agenda — Vista personalizada del usuario logueado.
 * Consume /api/erp/mi-agenda
 * Muestra:
 *   1. Eventos donde el usuario está asignado como personal
 *   2. Eventos próximos donde el usuario es propietario
 *   3. Recordatorios pendientes
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Calendar, Clock, MapPin, UserCog, PartyPopper, Bell, RefreshCcw, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import PageHeader from "../../components/PageHeader";

export default function MiAgenda() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/erp/mi-agenda`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const asignaciones = data.asignaciones_como_personal.eventos || [];
  const propios = data.eventos_propios_proximos.eventos || [];
  const recordatorios = data.recordatorios_pendientes.items || [];

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-8" data-testid="mi-agenda-page">
      <div className="flex items-start justify-between gap-2">
        <PageHeader
          title="Mi agenda"
          description={`Hola ${data.usuario.nombre || data.usuario.email}. Aquí ves los eventos y tareas asignados a ti.`}
        />
        <Button variant="outline" onClick={cargar} data-testid="btn-refresh-mi-agenda">
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </div>

      {/* KPIs personales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPICard icon={UserCog} label="Como personal" value={asignaciones.length} tone="cyan" tid="kpi-asignaciones" />
        <KPICard icon={PartyPopper} label="Eventos propios (60 días)" value={propios.length} tone="rose" tid="kpi-propios" />
        <KPICard icon={Bell} label="Recordatorios pendientes" value={recordatorios.length} tone="amber" tid="kpi-recordatorios" />
      </div>

      {/* Asignaciones como personal */}
      <Seccion titulo="Estoy asignado como personal" icon={UserCog} vacio="Aún no tienes asignaciones.">
        {asignaciones.map(e => (
          <Link key={e.evento_id} to={`/panel/eventos/${e.evento_id}`}
            className="block bg-white rounded-lg border border-stone-200 p-3 hover:border-rose-400 hover:shadow-sm transition"
            data-testid={`asig-${e.evento_id}`}>
            <div className="flex items-center gap-3">
              <div className="text-center min-w-[52px]">
                <p className="text-[10px] uppercase text-stone-500">{e.fecha ? new Date(e.fecha).toLocaleDateString("es-MX",{month:"short"}) : "—"}</p>
                <p className="font-display text-2xl text-rose-800">{e.fecha ? new Date(e.fecha).getDate() : "—"}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{e.nombre}</p>
                <p className="text-xs text-stone-500 truncate">
                  {e.mi_puesto ? <><span className="bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded text-[10px] uppercase font-medium mr-1">{e.mi_puesto}</span></> : null}
                  {e.mi_horario_inicio || "—"} → {e.mi_horario_fin || "—"} · {e.ubicacion || "sin ubicación"}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400" />
            </div>
          </Link>
        ))}
      </Seccion>

      {/* Eventos propios próximos */}
      <Seccion titulo="Mis eventos próximos (60 días)" icon={PartyPopper} vacio="Sin eventos en los próximos 60 días.">
        {propios.map(e => (
          <Link key={e.evento_id} to={`/panel/eventos/${e.evento_id}`}
            className="block bg-white rounded-lg border border-stone-200 p-3 hover:border-rose-400 hover:shadow-sm transition"
            data-testid={`propio-${e.evento_id}`}>
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-rose-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{e.nombre}</p>
                <p className="text-xs text-stone-500 truncate">
                  {e.fecha} {e.hora_inicio || ""} · {e.cliente_nombre || "sin cliente"} · {e.estatus}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400" />
            </div>
          </Link>
        ))}
      </Seccion>

      {/* Recordatorios */}
      <Seccion titulo="Recordatorios pendientes" icon={Bell} vacio="Sin recordatorios pendientes.">
        {recordatorios.map(r => (
          <div key={r.recordatorio_id || r._id} className="bg-white rounded-lg border border-stone-200 p-3" data-testid={`rec-${r.recordatorio_id}`}>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{r.titulo}</p>
                <p className="text-xs text-stone-500">
                  {r.fecha} {r.hora || ""} {r.prioridad ? `· ${r.prioridad}` : ""}
                </p>
              </div>
              {r.evento_id && (
                <Link to={`/panel/eventos/${r.evento_id}`} className="text-xs text-rose-700 hover:underline">Ver evento</Link>
              )}
            </div>
            {r.descripcion && <p className="text-xs text-stone-500 mt-1.5 ml-7">{r.descripcion}</p>}
          </div>
        ))}
      </Seccion>
    </div>
  );
}

const KPICard = ({ icon: Icon, label, value, tone = "stone", tid }) => {
  const tones = {
    cyan:  "bg-cyan-50 border-cyan-200 text-cyan-900",
    rose:  "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    stone: "bg-stone-50 border-stone-200 text-stone-900",
  }[tone];
  return (
    <Card className={`border ${tones}`} data-testid={tid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-70">{label}</p>
          <p className="font-display text-3xl mt-1">{value}</p>
        </div>
        <Icon className="w-6 h-6 opacity-60" />
      </CardContent>
    </Card>
  );
};

const Seccion = ({ titulo, icon: Icon, vacio, children }) => {
  const items = Array.isArray(children) ? children : [children];
  const filtered = items.filter(Boolean);
  return (
    <Card className="border-stone-200">
      <CardContent className="p-0">
        <div className="p-3 border-b border-stone-100 flex items-center gap-2">
          <Icon className="w-4 h-4 text-stone-600" />
          <h3 className="font-display text-base text-stone-900">{titulo}</h3>
          <span className="text-xs text-stone-500">({filtered.length})</span>
        </div>
        {filtered.length === 0 ? (
          <p className="p-6 text-center text-sm text-stone-400 italic">{vacio}</p>
        ) : (
          <div className="p-3 space-y-2">{filtered}</div>
        )}
      </CardContent>
    </Card>
  );
};
